import { Injectable, inject } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of, throwError, map, catchError, switchMap, tap, forkJoin, Observable, filter, concatMap, from } from 'rxjs';
import { formularActionsExt, formularActionsInt } from './formular.actions';
import { nutzerUnternehmenSelectionActions } from '../nutzer-unternehmen-selection/nutzer-unternehmen-selection.actions';
import { AwsControllerService } from '../../service/api/service/aws-controller.service';
import { EmptyBodyResponse, FileUploadDetails, GetPresignedUrlsResponse, PartEtag } from '../../model/aws-service.model';
import {
    selectFormularStoreActiveFormularAntragsId,
    selectFormularStoreActiveFormularAntragsnummer,
    selectFormularStoreFormularUrl,
    selectFormularStoreIsNewUnternehmen,
    selectFormularStoreOnlyStammdaten,
} from './formular.selectors';
import { HttpResponse } from '@angular/common/http';
import { selectActiveUnternehmenId } from '../nutzer-unternehmen-selection/nutzer-unternehmen-selection.selectors';
import { UnternehmenFoerderantraegeApiService } from '../../service/api/service/foerderantraege-unternehmen.service';
import { selectNebenantragsFoerderantragsId } from '../nebenantraege/nebenantraege.selectors';
import { UnternehmenNebenantraegeAPIService } from '../../service/api/service/nebenantraege-unternehmen.service';
import { FoerderantraegeCommonService } from '../../service/api/service/foerderantraege-common.service';
import { FormularUnternehmenService } from '../../service/api/service/formular-unternehmen.service';
import { PersistenceService } from '../../service/persistence.service';
import { GlobalExceptionHandlerService } from '../../service/api/service/global-exception-handler.service';
import { FormularHandlerService } from '../../service/formular-handler.service';
import { ANTRAGCANBESUBMITTEDSTATUS, AntragsType } from '../../model/formular.model';
import { NewAntragInfos, STAMMDATEN } from '../../model/foerderantraege.model';
import { AuthorizeService } from '../../service/authorize.service';
import { selectToken } from '../auth/auth.selectors';
import { AntragUnternehmenApiService } from '../../service/api/service/antrag-unternehmen.service';
import { DialogHelperService } from '../../service/helper/dialog.helper.service';
import { Router } from '@angular/router';
import { LogLevel, LogMessage } from '../../model/error-store.model';

/**
 * The effects for the formular store
 */
@Injectable()
export class FormularEffects {
    private chunkSize = 5 * 1024 * 1024;

    private actions$ = inject(Actions);
    private awsControllerService = inject(AwsControllerService);
    private unternehmenFoerderantraegeApiService = inject(UnternehmenFoerderantraegeApiService);
    private unternehmenNebenantraegeAPIService = inject(UnternehmenNebenantraegeAPIService);
    private foerderantraegeCommonService = inject(FoerderantraegeCommonService);
    private formUnternehmenService = inject(FormularUnternehmenService);
    private antragUnternehmenService = inject(AntragUnternehmenApiService);
    private persistenceService = inject(PersistenceService);
    private formularHandlerService = inject(FormularHandlerService);
    private globalExceptionHandlerService = inject(GlobalExceptionHandlerService);
    private store = inject(Store);
    private authService = inject(AuthorizeService);
    private dialogHelperService = inject(DialogHelperService);
    private router = inject(Router);

    /**
     * The Effect for the creation of a new antrag either for a foerdeantrag or a nebenantrag.
     */
    createNewAntragsnummer$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(formularActionsInt.postChangeCreateNewFormular),
            concatLatestFrom(() => [this.store.select(selectActiveUnternehmenId), this.store.select(selectNebenantragsFoerderantragsId)]),
            switchMap(([action, unternehmenId, hauptAntragsnummer]) => {
                this.dialogHelperService.createNewAntragGenerationDialog();
                this.formularHandlerService.clearFormularActionsInBus(null);
                this.persistenceService.writeToBus(formularActionsInt.setUpNewFormular({ shortName: action.shortName }));
                let returnNewAntragInfos: Observable<NewAntragInfos> = of({ antragsnummer: '', antragsId: 0 });
                let urlQueryParams: { [key: string]: string } = {};
                if (action.antragsType === AntragsType.Foerderantrag) {
                    returnNewAntragInfos = this.unternehmenFoerderantraegeApiService.createFoerderantrag(action.antragsArtId, unternehmenId);
                    urlQueryParams = { foerderart: action.antragsart.replaceAll(' ', '') };
                } else if (action.antragsType === AntragsType.Nebenantrag) {
                    const foerderantragId = action.foerderAntragsId ?? hauptAntragsnummer
                    returnNewAntragInfos = this.unternehmenNebenantraegeAPIService.createNebenantrag(action.antragsArtId, foerderantragId, unternehmenId);
                    urlQueryParams = { nebenantragsart: action.antragsart.replaceAll(' ', '') };
                }
                //After the antragsnummer was generated we get the antragsId for that antragsnummer
                return returnNewAntragInfos.pipe(
                    switchMap((newAntragInfos) => {
                        const { antragsnummer, antragsId } = newAntragInfos;
                        this.persistenceService.canAntragsnummerBeOpened(antragsnummer);
                        const returnAction = formularActionsInt.setActiveAntragsnummer({ antragsnummer, antragsId });
                        this.persistenceService.writeToBus(returnAction);
                        this.dialogHelperService.closeLastDialog();
                        this.formularHandlerService.navigateToFormular(urlQueryParams);
                        return of(returnAction);
                    }),
                    catchError((error) => {
                        this.dialogHelperService.closeLastDialog();
                        this.globalExceptionHandlerService.handleUnknownError(error);
                        return of(formularActionsInt.resetFormularState());
                    })
                );
            })
        )
    });

    createUnternehmen$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(formularActionsInt.changeCreateUnternehmen),
            switchMap(() => {
                this.dialogHelperService.createNewAntragGenerationDialog();
                return this.foerderantraegeCommonService.createFoerderantragForUnternehmensregistrierung().pipe(
                    map(newAntragInfos => {
                        const { antragsnummer, antragsId } = newAntragInfos;
                        this.persistenceService.canAntragsnummerBeOpened(antragsnummer);
                        this.formularHandlerService.clearFormularActionsInBus();
                        const returnAction = formularActionsInt.changeCreateUnternehmenSuccess({ antragsnummer, antragsId });
                        this.persistenceService.writeToBus(returnAction);
                        this.dialogHelperService.closeLastDialog();
                        this.formularHandlerService.navigateToFormular({ foerderart: 'Unternehmensregistrierung' });
                        return returnAction;
                    }),
                    catchError((error) => {
                        this.dialogHelperService.closeLastDialog();
                        this.globalExceptionHandlerService.handleUnknownError(error);
                        return of(formularActionsInt.resetFormularState());
                    })
                );
            })
        )
    });

    /**
     * Effect that is triggered when the FE tab containing the form initializes.
     * Only for forms that are not the Unternehmensregistrierung form fetching form content is dispatched.
     */
    initializeNewTab$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(formularActionsInt.initializeNewTab),
            concatLatestFrom(() => [this.store.select(selectFormularStoreIsNewUnternehmen)]),
            filter(([, isNewUnternehmen]) => !isNewUnternehmen),
            switchMap(() => of(formularActionsInt.changeFormContent())),
        );
    });

    /**
     * Effect that updates the form content
     */
    updateFormContent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(formularActionsInt.changeFormContent),
            concatLatestFrom(() => [
                this.store.select(selectActiveUnternehmenId),
                this.store.select(selectFormularStoreActiveFormularAntragsnummer),
                this.store.select(selectFormularStoreOnlyStammdaten),
            ]),
            filter(([, unternehmenId, activeAntragsnummer]) => activeAntragsnummer.trim() !== '' && unternehmenId !== 0),
            switchMap(([, unternehmenId, activeAntragsnummer, onlyStammdaten]) => {
                return this.formUnternehmenService
                    .getFormContent(unternehmenId, activeAntragsnummer, onlyStammdaten)
                    .pipe(map((formContent) => formularActionsInt.setFormContent({ formContent })));
            }),
            catchError((error) => {
                this.globalExceptionHandlerService.handleUnknownError(error);
                this.router.navigate(["/alle-antraege"]);
                return of(formularActionsInt.changeFormContentFailure());
            }),
        );
    });

    updateExistingFormular$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(formularActionsInt.postChangeExistingFormular),
            switchMap((action) => {
                this.persistenceService.canAntragsnummerBeOpened(action.antragsnummer);
                this.formularHandlerService.clearFormularActionsInBus();
                const returnAction = formularActionsInt.setExistingFormular({
                    antragsnummer: action.antragsnummer,
                    formularShortname: action.formularShortname,
                    antragsId: action.antragsId
                });
                this.persistenceService.writeToBus(returnAction);
                return of(returnAction);
            }),
        );
    });


    updateExistingFormularInternal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(formularActionsInt.postChangeExistingFormularInternal),
            concatLatestFrom(() => [
                this.store.select(selectFormularStoreActiveFormularAntragsnummer),
                this.store.select(selectFormularStoreActiveFormularAntragsId),
                this.store.select(selectFormularStoreFormularUrl),
                this.store.select(selectFormularStoreIsNewUnternehmen),
            ]),
            switchMap(([, antragsnummer, antragsId, url, isNewUnternehmen]) => {
                const shortName = url.split(RegExp("[&?]shortname="))[1].split("&")[0];
                if (isNewUnternehmen) {
                    this.formularHandlerService.clearFormularActionsInBus();
                    const returnAction = formularActionsInt.changeCreateUnternehmenSuccess({ antragsnummer, antragsId });
                    this.persistenceService.writeToBus(returnAction);
                    return of(returnAction);
                }
                return of(formularActionsInt.postChangeExistingFormular({ antragsnummer, antragsArt: "", formularShortname: shortName, antragsId }));
            })
        )
    });

    updateUnternehmensname$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(formularActionsInt.updateUnternehmensname),
            concatLatestFrom(() => this.store.select(selectFormularStoreActiveFormularAntragsnummer)),
            switchMap(([action, antragsnummer]) =>
                this.foerderantraegeCommonService.updateUnternehmensnameAfterUnternehmensRegistrierung(antragsnummer, action.unternehmensname).pipe(
                    map(() => nutzerUnternehmenSelectionActions.updateNutzerUnternehmenSelectionList())
                )
            )
        );
    });

    updateFormularSubmitStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(formularActionsExt.updateFormularSubmitStatus),
            concatLatestFrom(() => [
                this.store.select(selectFormularStoreActiveFormularAntragsnummer),
                this.store.select(selectActiveUnternehmenId),
                this.store.select(selectFormularStoreActiveFormularAntragsId),
            ]),
            switchMap(([, antragsnummer, unternehmenId, antragsId]) => {
                return this.antragUnternehmenService.updateAntragsstatusEinreichung(antragsId, unternehmenId).pipe(
                    map(() => {
                        const returnAction: Action = formularActionsExt.changeReloadDataForSubmittedFormular({ antragsnummer, antragsId });
                        this.persistenceService.writeToBus(returnAction);
                        return returnAction;
                    })
                );
            })
        )
    });

    updateAntragCanBeSubmitted$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(formularActionsInt.updateAntragCanBeSubmitted),
            concatLatestFrom(() => [
                this.store.select(selectActiveUnternehmenId),
                this.store.select(selectFormularStoreActiveFormularAntragsId),
                this.store.select(selectFormularStoreIsNewUnternehmen),
            ]),
            switchMap(([, unternehmenId, antragsId, isNewUnternehmen]) => {
                if (isNewUnternehmen) {
                    return of(formularActionsInt.setAntragCanBeSubmitted({ antragCanBeSubmitted: true }));
                }
                return this.unternehmenFoerderantraegeApiService.getFoerderantragById(antragsId, unternehmenId).pipe(
                    map((foerderantrag) => {
                        const antragCanBeSubmitted = ANTRAGCANBESUBMITTEDSTATUS.includes(foerderantrag.status);
                        return formularActionsInt.setAntragCanBeSubmitted({ antragCanBeSubmitted });
                    })
                )
            }
            ),
        );
    });

    updateSubmitFailure$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(formularActionsInt.updateSubmitFailure),
            concatLatestFrom(() => [
                this.store.select(selectFormularStoreActiveFormularAntragsnummer),
            ]),
            switchMap(([, antragsnummer]) => {
                const logMessage: LogMessage = {
                    message: "For Antrag " + antragsnummer + " a submit error occurred in the FMS, and the upload of the submit JSON was canceled.",
                    timestamp: new Date(Date.now()).toISOString(),
                    level: LogLevel.ERROR,
                };
                this.formularHandlerService.clearFormularActionsInBus(antragsnummer)
                return this.globalExceptionHandlerService.writeToServer(logMessage).pipe(
                    map(() => formularActionsInt.setSubmitFailure()),
                );
            })
        );
    })

    //TODO: FFA-305 remove hardcoded Stammdaten Formular
    /**
     * Effect to create a Stammdaten form for an existing company
     */
    updateUnternehmenStammdaten$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(formularActionsInt.changeUnternehmenStammdaten),
            switchMap(() => {
                return of(
                    formularActionsInt.postChangeCreateNewFormular({
                        shortName: STAMMDATEN.gatewayShortname,
                        antragsArtId: STAMMDATEN.id,
                        antragsType: AntragsType.Foerderantrag,
                        antragsart: STAMMDATEN.bezeichnung,
                    }),
                );
            }),
        );
    });

    /**
     * Effect that starts the upload of a file into the s3 bucket
     */
    uploadInitialization$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(formularActionsExt.changeInitializationFileUpload),
            concatLatestFrom(() => [this.store.select(selectFormularStoreActiveFormularAntragsnummer), this.store.select(selectToken)]),
            concatMap(([action, formularAntragsnummer, authToken]) => {
                return from(this.authService.checkTokenValidity(authToken || '')).pipe(
                    switchMap((tokenStillValid) => {
                        if (tokenStillValid) {
                            let fileName = action.file.name;
                            if (action.formFileType === 'attachment') {
                                fileName = formularAntragsnummer + '_' + fileName;
                            }
                            return this.awsControllerService.initializeUpload(fileName).pipe(
                                map((initializeResponse) => {
                                    const fileUploadDetails = {
                                        file: action.file,
                                        formFileType: action.formFileType,
                                        htmlFieldId: action.htmlFieldId,
                                        status: action.status,
                                        jsonRecordId: action.jsonRecordId,
                                        antragsnummer: formularAntragsnummer,
                                        fileDetails: initializeResponse,
                                        returnMessagePort: action.returnMessagePort,
                                    } satisfies FileUploadDetails;
                                    return formularActionsExt.updateGeneratePresignedUrls({ initializeResponse, fileUploadDetails });
                                }),
                                catchError(() => {
                                    this.formularHandlerService.handleReturnMessage(action.returnMessagePort, false);
                                    return of(formularActionsExt.setInitializationFileUploadFailure());
                                }),
                            );
                        } else {
                            return of(formularActionsExt.awaitRefresh());
                        }
                    }),
                    catchError(() => {
                        return of(formularActionsExt.awaitRefresh());
                    }),
                );
            }),
        );
    });

    /**
     * The effect that triggers the generation of presigned urls and the upload of the file parts
     */
    initializeMultipartUpload$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(formularActionsExt.updateGeneratePresignedUrls),
            concatMap((action) => {
                const { fileId, fileKey } = action.initializeResponse;
                const parts = Math.ceil(action.fileUploadDetails.file.size / this.chunkSize);

                return this.awsControllerService.getPresignedUrls(fileKey, fileId, parts).pipe(
                    switchMap((presignedUrls) =>
                        of(
                            formularActionsExt.changeGeneratePresignedUrlsUpload({
                                presignedUrls,
                                fileUploadDetails: action.fileUploadDetails,
                                partsMax: parts + 1,
                            }),
                        ),
                    ),
                    catchError(() => {
                        this.formularHandlerService.handleReturnMessage(action.fileUploadDetails.returnMessagePort, false);
                        return of(formularActionsExt.setGeneratePresignedUrlsFailure());
                    }),
                );
            }),
        );
    });

    /**
     * The effect that finalizes the upload of the file into the s3 bucket
     */
    finalizeUpload$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(formularActionsExt.changeGeneratePresignedUrlsUpload),
            concatMap(({ presignedUrls, fileUploadDetails }) =>
                this.uploadFileParts(fileUploadDetails.file, presignedUrls).pipe(
                    switchMap(partEtags =>
                        this.awsControllerService.finalizeUpload(
                            fileUploadDetails.formFileType,
                            fileUploadDetails.antragsnummer,
                            fileUploadDetails.fileDetails.fileKey,
                            fileUploadDetails.fileDetails.fileId,
                            partEtags,
                            fileUploadDetails.htmlFieldId,
                            fileUploadDetails.status,
                            fileUploadDetails.jsonRecordId,
                        ).pipe(
                            map(() => {
                                this.formularHandlerService.handleReturnMessage(fileUploadDetails.returnMessagePort, true);
                                if (fileUploadDetails.htmlFieldId === 'btnSend' && fileUploadDetails.status === 'submit') {
                                    this.formularHandlerService.handleFormSubmitSuccess();
                                }
                                return formularActionsExt.setFinializeUpload();
                            }),
                            catchError(() => {
                                this.formularHandlerService.handleReturnMessage(fileUploadDetails.returnMessagePort, false);
                                return of(formularActionsExt.setFinializeUploadFailure());
                            })
                        )
                    ),
                    catchError(() => {
                        this.formularHandlerService.handleReturnMessage(fileUploadDetails.returnMessagePort, false);
                        return of(formularActionsExt.setGeneratePresignedUrlsUploadFailure());
                    }),
                ),
            ),
        );
    });

    private uploadFileParts(file: File, presignedUrls: GetPresignedUrlsResponse): Observable<PartEtag[]> {
        const partUploads = presignedUrls.parts.map((partUrl, index) => {
            const start = index * this.chunkSize;
            const end = Math.min(start + this.chunkSize, file.size);
            const filePart = file.slice(start, end);
            const currentPartNumber = index + 1;

            return this.awsControllerService.uploadPartToS3(partUrl.signedUrl, filePart).pipe(
                map(
                    (response) =>
                    ({
                        PartNumber: partUrl.PartNumber,
                        ETag: this.extractEtag(response),
                    } as PartEtag),
                ),
                catchError(() => {
                    return throwError(() => new Error(`Fehler beim Hochladen des  ${currentPartNumber} Parts der Datei`));
                }),
                tap(() => {
                    this.store.dispatch(formularActionsExt.incrementUploadedParts());
                }),
            );
        });

        return forkJoin(partUploads);
    }

    private extractEtag(response: HttpResponse<EmptyBodyResponse>): string {
        return response.headers.get('ETag')?.replaceAll('"', '') ?? '';
    }
}
